<template>
  <div class="page-content reports-page">
    <breadcrumb-index :items="breadcrumbs" icon="bar-chart-2" badge="Reports" badge-variant="badge-primary">
      <template #actions>
        <div class="d-flex gap-2">
          <button @click="refreshAll" class="btn btn-outline-secondary btn-sm" :disabled="loading">
            <vue-feather type="refresh-cw" size="14" class="me-1" :class="{ 'spin': loading }" />
            Refresh
          </button>
          <button @click="exportCurrentReport" class="btn btn-outline-primary btn-sm" :disabled="!hasData">
            <vue-feather type="download" size="14" class="me-1" />
            Export CSV
          </button>
        </div>
      </template>
    </breadcrumb-index>

    <!-- Role Badge & Date Range -->
    <div class="report-header">
      <div class="report-header-left">
        <span class="role-badge" :class="'role-' + userRole">{{ formatRole(userRole) }}</span>
        <span class="report-date">{{ currentDate }}</span>
      </div>
      <div class="report-header-right">
        <select v-model="globalDateRange" @change="onDateRangeChange" class="filter-select-modern">
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="90">Last 90 Days</option>
          <option value="180">Last 6 Months</option>
          <option value="365">Last Year</option>
          <option value="all">All Time</option>
        </select>
      </div>
    </div>

    <!-- Report Navigation Tabs -->
    <div class="report-tabs">
      <button v-for="tab in availableTabs" :key="tab.key"
        :class="['report-tab', { active: activeTab === tab.key }]"
        @click="activeTab = tab.key">
        <vue-feather :type="tab.icon" size="16" />
        <span>{{ tab.label }}</span>
      </button>
    </div>

    <!-- ==================== CONTENT REPORT (All Roles) ==================== -->
    <div v-if="activeTab === 'content'" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="file-text" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ contentReport.totalExams }}</span>
            <span class="stat-label">Total Exams</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="help-circle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ contentReport.totalQuestions }}</span>
            <span class="stat-label">Total Questions</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="book-open" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ contentReport.totalNotes }}</span>
            <span class="stat-label">Total Notes</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="layers" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ contentReport.totalFlashcards }}</span>
            <span class="stat-label">Flashcards</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="card-title mb-0">Content Created Over Time</h6>
              <div class="chart-period-btns">
                <button v-for="p in ['7d','30d','90d']" :key="p"
                  :class="['period-btn', { active: contentChartPeriod === p }]"
                  @click="changeContentChartPeriod(p)">{{ p }}</button>
              </div>
            </div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars">
                  <div v-for="(bar, i) in contentReport.chartData" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar" :style="{ height: bar.height + '%' }" :title="bar.label + ': ' + bar.value">
                      <span class="bar-tooltip">{{ bar.value }}</span>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Content by Status</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="s in contentReport.statusBreakdown" :key="s.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + s.color"></span> {{ s.label }}
                  </div>
                  <div class="breakdown-value">{{ s.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + s.color" :style="{ width: s.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Content Details Table -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Content Breakdown by Exam</h6>
          <button @click="exportContentReport" class="btn btn-outline-primary btn-sm">
            <vue-feather type="download" size="14" class="me-1" /> Export
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Exam</th>
                  <th>Subjects</th>
                  <th>Topics</th>
                  <th>Lessons</th>
                  <th>Questions</th>
                  <th>Notes</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in contentReport.examBreakdown" :key="item.id">
                  <td class="fw-medium">{{ item.name }}</td>
                  <td>{{ item.subjects }}</td>
                  <td>{{ item.topics }}</td>
                  <td>{{ item.lessons }}</td>
                  <td>{{ item.questions }}</td>
                  <td>{{ item.notes }}</td>
                  <td><span :class="['status-badge', 'status-' + item.status]">{{ item.status }}</span></td>
                </tr>
                <tr v-if="!contentReport.examBreakdown.length">
                  <td colspan="7" class="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== USER ANALYTICS (Super Admin Only) ==================== -->
    <div v-if="activeTab === 'users' && isSuperAdmin" class="report-section">
      <div class="stats-grid stats-6">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="users" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ userReport.totalUsers }}</span>
            <span class="stat-label">Total Users</span>
          </div>
          <div class="stat-trend trend-up" v-if="userReport.usersTrend > 0">
            <vue-feather type="trending-up" size="14" /> {{ userReport.usersTrend }}%
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="user-check" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ userReport.activeUsers }}</span>
            <span class="stat-label">Active Users</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="user-plus" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ userReport.newUsers }}</span>
            <span class="stat-label">New Users</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="award" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ userReport.premiumUsers }}</span>
            <span class="stat-label">Premium Users</span>
          </div>
        </div>
        <div class="stat-card stat-purple">
          <div class="stat-icon"><vue-feather type="user-x" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ userReport.churnedUsers }}</span>
            <span class="stat-label">Churned</span>
          </div>
        </div>
        <div class="stat-card stat-rose">
          <div class="stat-icon"><vue-feather type="percent" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ userReport.retentionRate }}%</span>
            <span class="stat-label">Retention</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="card-title mb-0">User Registrations</h6>
              <div class="chart-period-btns">
                <button v-for="p in ['7d','30d','90d']" :key="p"
                  :class="['period-btn', { active: userChartPeriod === p }]"
                  @click="changeUserChartPeriod(p)">{{ p }}</button>
              </div>
            </div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars">
                  <div v-for="(bar, i) in userReport.registrationChart" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar chart-bar-blue" :style="{ height: bar.height + '%' }" :title="bar.label + ': ' + bar.value">
                      <span class="bar-tooltip">{{ bar.value }}</span>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">User Segments</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="seg in userReport.segments" :key="seg.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + seg.color"></span> {{ seg.label }}
                  </div>
                  <div class="breakdown-value">{{ seg.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + seg.color" :style="{ width: seg.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Users Table -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Top Active Users</h6>
          <button @click="exportUserReport" class="btn btn-outline-primary btn-sm">
            <vue-feather type="download" size="14" class="me-1" /> Export
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Tests Taken</th>
                  <th>Avg Score</th>
                  <th>Last Active</th>
                  <th>Plan</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in userReport.topUsers" :key="u.id">
                  <td class="fw-medium">{{ u.name }}</td>
                  <td class="text-muted">{{ u.email }}</td>
                  <td>{{ u.testsTaken }}</td>
                  <td>
                    <span :class="u.avgScore >= 70 ? 'text-success' : 'text-danger'">{{ u.avgScore }}%</span>
                  </td>
                  <td>{{ timeAgo(u.lastActive) }}</td>
                  <td><span :class="['badge', u.plan === 'premium' ? 'bg-warning' : 'bg-secondary']">{{ u.plan }}</span></td>
                </tr>
                <tr v-if="!userReport.topUsers.length">
                  <td colspan="6" class="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== TEST PERFORMANCE (All Roles) ==================== -->
    <div v-if="activeTab === 'performance'" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="clipboard" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ perfReport.totalTestsTaken }}</span>
            <span class="stat-label">Tests Taken</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="trending-up" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ perfReport.avgScore }}%</span>
            <span class="stat-label">Avg Score</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="award" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ perfReport.passRate }}%</span>
            <span class="stat-label">Pass Rate</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="clock" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ perfReport.avgTimeMins }}m</span>
            <span class="stat-label">Avg Duration</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="card-title mb-0">Score Distribution</h6>
            </div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars">
                  <div v-for="(bar, i) in perfReport.scoreDistribution" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar" :class="bar.value >= 70 ? 'chart-bar-green' : bar.value >= 40 ? 'chart-bar-yellow' : 'chart-bar-red'"
                      :style="{ height: bar.height + '%' }" :title="bar.label + ': ' + bar.value">
                      <span class="bar-tooltip">{{ bar.value }}</span>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Difficulty Analysis</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="d in perfReport.difficultyBreakdown" :key="d.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + d.color"></span> {{ d.label }}
                  </div>
                  <div class="breakdown-value">{{ d.avgScore }}%</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + d.color" :style="{ width: d.avgScore + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Test Performance Table -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Test-wise Performance</h6>
          <div class="d-flex gap-2">
            <select v-model="perfExamFilter" class="filter-select-modern filter-sm">
              <option value="">All Exams</option>
              <option v-for="e in examOptions" :key="e.id" :value="e.id">{{ e.name }}</option>
            </select>
            <button @click="exportPerfReport" class="btn btn-outline-primary btn-sm">
              <vue-feather type="download" size="14" class="me-1" /> Export
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Test Name</th>
                  <th>Exam</th>
                  <th>Attempts</th>
                  <th>Avg Score</th>
                  <th>Pass Rate</th>
                  <th>Avg Time</th>
                  <th>Completion</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in filteredPerfData" :key="t.id">
                  <td class="fw-medium">{{ t.name }}</td>
                  <td><span class="badge bg-light text-dark">{{ t.examName }}</span></td>
                  <td>{{ t.attempts }}</td>
                  <td>
                    <div class="score-bar-inline">
                      <div class="score-fill" :class="t.avgScore >= 70 ? 'fill-green' : t.avgScore >= 40 ? 'fill-yellow' : 'fill-red'"
                        :style="{ width: t.avgScore + '%' }"></div>
                      <span>{{ t.avgScore }}%</span>
                    </div>
                  </td>
                  <td>{{ t.passRate }}%</td>
                  <td>{{ t.avgTimeMins }}m</td>
                  <td>{{ t.completionRate }}%</td>
                </tr>
                <tr v-if="!filteredPerfData.length">
                  <td colspan="7" class="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== QUESTION ANALYTICS (All Roles) ==================== -->
    <div v-if="activeTab === 'questions'" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="help-circle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ questionReport.totalQuestions }}</span>
            <span class="stat-label">Total Questions</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="check-circle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ questionReport.avgCorrectRate }}%</span>
            <span class="stat-label">Avg Correct Rate</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="alert-triangle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ questionReport.hardestCount }}</span>
            <span class="stat-label">Hard Questions</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="zap" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ questionReport.avgTimePerQ }}s</span>
            <span class="stat-label">Avg Time/Question</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Questions by Difficulty</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="d in questionReport.byDifficulty" :key="d.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + d.color"></span> {{ d.label }}
                  </div>
                  <div class="breakdown-value">{{ d.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + d.color" :style="{ width: d.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Questions by Type</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="t in questionReport.byType" :key="t.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + t.color"></span> {{ t.label }}
                  </div>
                  <div class="breakdown-value">{{ t.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + t.color" :style="{ width: t.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Most Missed Questions -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Most Missed Questions</h6>
          <button @click="exportQuestionReport" class="btn btn-outline-primary btn-sm">
            <vue-feather type="download" size="14" class="me-1" /> Export
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Subject</th>
                  <th>Difficulty</th>
                  <th>Attempts</th>
                  <th>Correct %</th>
                  <th>Avg Time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="q in questionReport.mostMissed" :key="q.id">
                  <td class="fw-medium text-truncate" style="max-width: 250px;">{{ q.text }}</td>
                  <td><span class="badge bg-light text-dark">{{ q.subject }}</span></td>
                  <td><span :class="['difficulty-badge', 'diff-' + q.difficulty]">{{ q.difficulty }}</span></td>
                  <td>{{ q.attempts }}</td>
                  <td>
                    <span :class="q.correctRate < 30 ? 'text-danger' : q.correctRate < 60 ? 'text-warning' : 'text-success'">
                      {{ q.correctRate }}%
                    </span>
                  </td>
                  <td>{{ q.avgTime }}s</td>
                </tr>
                <tr v-if="!questionReport.mostMissed.length">
                  <td colspan="6" class="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== APPROVAL & REVIEW REPORT (Admin + Reviewer) ==================== -->
    <div v-if="activeTab === 'approvals' && (isAdmin || isReviewer)" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="clock" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ approvalReport.pending }}</span>
            <span class="stat-label">Pending</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="check-circle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ approvalReport.approved }}</span>
            <span class="stat-label">Approved</span>
          </div>
        </div>
        <div class="stat-card stat-danger">
          <div class="stat-icon"><vue-feather type="x-circle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ approvalReport.rejected }}</span>
            <span class="stat-label">Rejected</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="clock" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ approvalReport.avgReviewTime }}</span>
            <span class="stat-label">Avg Review Time</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header"><h6 class="card-title mb-0">Approval Activity Over Time</h6></div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars chart-stacked">
                  <div v-for="(bar, i) in approvalReport.chartData" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar-stack">
                      <div class="chart-bar chart-bar-green" :style="{ height: bar.approvedHeight + '%' }" :title="'Approved: ' + bar.approved"></div>
                      <div class="chart-bar chart-bar-red" :style="{ height: bar.rejectedHeight + '%' }" :title="'Rejected: ' + bar.rejected"></div>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">By Content Type</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="t in approvalReport.byType" :key="t.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + t.color"></span> {{ t.label }}
                  </div>
                  <div class="breakdown-value">{{ t.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + t.color" :style="{ width: t.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reviewer-specific: My Reviews -->
      <div v-if="isReviewer" class="card mt-3">
        <div class="card-header"><h6 class="card-title mb-0">My Review History</h6></div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Type</th>
                  <th>Decision</th>
                  <th>Review Time</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in approvalReport.myReviews" :key="r.id">
                  <td class="fw-medium">{{ r.title }}</td>
                  <td><span class="badge bg-light text-dark">{{ r.type }}</span></td>
                  <td>
                    <span :class="['status-badge', r.decision === 'approved' ? 'status-active' : 'status-archived']">
                      {{ r.decision }}
                    </span>
                  </td>
                  <td>{{ r.reviewTime }}</td>
                  <td>{{ formatDate(r.date) }}</td>
                </tr>
                <tr v-if="!approvalReport.myReviews.length">
                  <td colspan="5" class="text-center text-muted py-4">No reviews yet</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Admin: Reviewer Performance -->
      <div v-if="isSuperAdmin || (isAdmin && !isReviewer)" class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Reviewer Performance</h6>
          <button @click="exportApprovalReport" class="btn btn-outline-primary btn-sm">
            <vue-feather type="download" size="14" class="me-1" /> Export
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Reviewer</th>
                  <th>Reviewed</th>
                  <th>Approved</th>
                  <th>Rejected</th>
                  <th>Avg Time</th>
                  <th>Accuracy</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rv in approvalReport.reviewerPerformance" :key="rv.id">
                  <td class="fw-medium">{{ rv.name }}</td>
                  <td>{{ rv.totalReviewed }}</td>
                  <td class="text-success">{{ rv.approved }}</td>
                  <td class="text-danger">{{ rv.rejected }}</td>
                  <td>{{ rv.avgTime }}</td>
                  <td>
                    <div class="score-bar-inline">
                      <div class="score-fill fill-green" :style="{ width: rv.accuracy + '%' }"></div>
                      <span>{{ rv.accuracy }}%</span>
                    </div>
                  </td>
                </tr>
                <tr v-if="!approvalReport.reviewerPerformance.length">
                  <td colspan="6" class="text-center text-muted py-4">No data available</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== ADMIN ACTIVITY (Super Admin Only) ==================== -->
    <div v-if="activeTab === 'activity' && isSuperAdmin" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="activity" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ activityReport.totalActions }}</span>
            <span class="stat-label">Total Actions</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="user-check" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ activityReport.activeAdmins }}</span>
            <span class="stat-label">Active Admins</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="edit-3" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ activityReport.contentEdits }}</span>
            <span class="stat-label">Content Edits</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="log-in" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ activityReport.totalLogins }}</span>
            <span class="stat-label">Admin Logins</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header"><h6 class="card-title mb-0">Admin Activity Timeline</h6></div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars">
                  <div v-for="(bar, i) in activityReport.chartData" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar chart-bar-purple" :style="{ height: bar.height + '%' }" :title="bar.label + ': ' + bar.value">
                      <span class="bar-tooltip">{{ bar.value }}</span>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Actions by Type</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="a in activityReport.byAction" :key="a.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + a.color"></span> {{ a.label }}
                  </div>
                  <div class="breakdown-value">{{ a.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + a.color" :style="{ width: a.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Activity Log Table -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Detailed Activity Log</h6>
          <div class="d-flex gap-2">
            <select v-model="activityAdminFilter" class="filter-select-modern filter-sm">
              <option value="">All Admins</option>
              <option v-for="a in activityReport.adminList" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
            <button @click="exportActivityReport" class="btn btn-outline-primary btn-sm">
              <vue-feather type="download" size="14" class="me-1" /> Export
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Details</th>
                  <th>IP Address</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in filteredActivityLogs" :key="log.id">
                  <td class="fw-medium">{{ log.adminName }}</td>
                  <td><span :class="['action-badge', 'action-' + log.actionType]">{{ log.action }}</span></td>
                  <td>{{ log.resource }}</td>
                  <td class="text-muted text-truncate" style="max-width: 200px;">{{ log.details }}</td>
                  <td class="text-muted">{{ log.ipAddress }}</td>
                  <td>{{ formatDateTime(log.timestamp) }}</td>
                </tr>
                <tr v-if="!filteredActivityLogs.length">
                  <td colspan="6" class="text-center text-muted py-4">No activity logs</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== REVENUE & SUBSCRIPTION (Super Admin Only) ==================== -->
    <div v-if="activeTab === 'revenue' && isSuperAdmin" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="dollar-sign" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">₹{{ revenueReport.totalRevenue }}</span>
            <span class="stat-label">Total Revenue</span>
          </div>
          <div class="stat-trend trend-up" v-if="revenueReport.revenueTrend > 0">
            <vue-feather type="trending-up" size="14" /> {{ revenueReport.revenueTrend }}%
          </div>
        </div>
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="credit-card" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ revenueReport.totalSubscriptions }}</span>
            <span class="stat-label">Subscriptions</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="repeat" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ revenueReport.renewalRate }}%</span>
            <span class="stat-label">Renewal Rate</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="target" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">₹{{ revenueReport.arpu }}</span>
            <span class="stat-label">ARPU</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="card-title mb-0">Revenue Trend</h6>
              <div class="chart-period-btns">
                <button v-for="p in ['7d','30d','90d']" :key="p"
                  :class="['period-btn', { active: revenueChartPeriod === p }]"
                  @click="changeRevenueChartPeriod(p)">{{ p }}</button>
              </div>
            </div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars">
                  <div v-for="(bar, i) in revenueReport.chartData" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar chart-bar-green" :style="{ height: bar.height + '%' }" :title="bar.label + ': ₹' + bar.value">
                      <span class="bar-tooltip">₹{{ bar.value }}</span>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Plan Distribution</h6></div>
            <div class="card-body">
              <div class="breakdown-list">
                <div class="breakdown-item" v-for="p in revenueReport.planBreakdown" :key="p.label">
                  <div class="breakdown-label">
                    <span class="breakdown-dot" :class="'dot-' + p.color"></span> {{ p.label }}
                  </div>
                  <div class="breakdown-value">{{ p.count }}</div>
                  <div class="breakdown-bar"><div class="bar-fill" :class="'bar-' + p.color" :style="{ width: p.percent + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Transactions -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Recent Transactions</h6>
          <button @click="exportRevenueReport" class="btn btn-outline-primary btn-sm">
            <vue-feather type="download" size="14" class="me-1" /> Export
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Payment Method</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tx in revenueReport.transactions" :key="tx.id">
                  <td class="fw-medium">{{ tx.userName }}</td>
                  <td><span class="badge bg-primary">{{ tx.plan }}</span></td>
                  <td>₹{{ tx.amount }}</td>
                  <td>
                    <span :class="['status-badge', tx.status === 'success' ? 'status-active' : tx.status === 'pending' ? 'status-draft' : 'status-archived']">
                      {{ tx.status }}
                    </span>
                  </td>
                  <td>{{ tx.paymentMethod }}</td>
                  <td>{{ formatDate(tx.date) }}</td>
                </tr>
                <tr v-if="!revenueReport.transactions.length">
                  <td colspan="6" class="text-center text-muted py-4">No transactions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== SYSTEM & INFRASTRUCTURE (Super Admin Only) ==================== -->
    <div v-if="activeTab === 'system' && isSuperAdmin" class="report-section">
      <div class="stats-grid stats-4">
        <div class="stat-card stat-success">
          <div class="stat-icon"><vue-feather type="server" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ systemReport.uptime }}</span>
            <span class="stat-label">Uptime</span>
          </div>
        </div>
        <div class="stat-card stat-primary">
          <div class="stat-icon"><vue-feather type="zap" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ systemReport.avgResponseTime }}ms</span>
            <span class="stat-label">Avg Response</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><vue-feather type="alert-circle" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ systemReport.errorCount }}</span>
            <span class="stat-label">Errors (24h)</span>
          </div>
        </div>
        <div class="stat-card stat-info">
          <div class="stat-icon"><vue-feather type="hard-drive" size="20" /></div>
          <div class="stat-text">
            <span class="stat-value">{{ systemReport.storageUsed }}</span>
            <span class="stat-label">Storage Used</span>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header"><h6 class="card-title mb-0">API Requests (24h)</h6></div>
            <div class="card-body">
              <div class="chart-container">
                <div class="mini-chart-bars">
                  <div v-for="(bar, i) in systemReport.apiChart" :key="i" class="chart-bar-wrapper">
                    <div class="chart-bar" :style="{ height: bar.height + '%' }" :title="bar.label + ': ' + bar.value">
                      <span class="bar-tooltip">{{ bar.value }}</span>
                    </div>
                    <span class="bar-label">{{ bar.label }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-4">
          <div class="card h-100">
            <div class="card-header"><h6 class="card-title mb-0">Resource Usage</h6></div>
            <div class="card-body">
              <div class="resource-item mb-3" v-for="r in systemReport.resources" :key="r.label">
                <div class="d-flex justify-content-between mb-1">
                  <span class="small text-muted">{{ r.label }}</span>
                  <span class="small fw-medium">{{ r.used }} / {{ r.total }}</span>
                </div>
                <div class="resource-bar">
                  <div class="resource-fill" :class="r.percent > 80 ? 'fill-danger' : 'fill-primary'"
                    :style="{ width: r.percent + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Log -->
      <div class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="card-title mb-0">Recent Error Log</h6>
          <button @click="exportSystemReport" class="btn btn-outline-primary btn-sm">
            <vue-feather type="download" size="14" class="me-1" /> Export
          </button>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Error</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Count</th>
                  <th>Last Occurred</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="err in systemReport.errors" :key="err.id">
                  <td class="fw-medium text-danger">{{ err.message }}</td>
                  <td class="text-muted">{{ err.endpoint }}</td>
                  <td><span class="badge bg-danger">{{ err.statusCode }}</span></td>
                  <td>{{ err.count }}</td>
                  <td>{{ formatDateTime(err.lastOccurred) }}</td>
                </tr>
                <tr v-if="!systemReport.errors.length">
                  <td colspan="5" class="text-center text-muted py-4">No errors — looking good</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="report-loading">
      <div class="spinner"></div>
      <span>Loading reports...</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'

export default {
  name: 'ReportsPage',
  setup() {
    const authStore = useAuthStore()
    const user = computed(() => authStore.user)
    const userRole = computed(() => authStore.userRole)
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)
    const isAdmin = computed(() => authStore.isAdmin)
    const isReviewer = computed(() => authStore.isReviewer)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Reports', active: true }
    ]

    const loading = ref(false)
    const activeTab = ref('content')
    const globalDateRange = ref('30')

    // Chart periods
    const contentChartPeriod = ref('30d')
    const userChartPeriod = ref('30d')
    const revenueChartPeriod = ref('30d')

    // Filters
    const perfExamFilter = ref('')
    const activityAdminFilter = ref('')
    const examOptions = ref([])

    const currentDate = computed(() => {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      })
    })

    // Tab definitions with role gating
    const allTabs = [
      { key: 'content', label: 'Content', icon: 'file-text', roles: ['super_admin', 'admin', 'reviewer', 'content_manager'] },
      { key: 'performance', label: 'Performance', icon: 'trending-up', roles: ['super_admin', 'admin', 'reviewer'] },
      { key: 'questions', label: 'Questions', icon: 'help-circle', roles: ['super_admin', 'admin', 'reviewer'] },
      { key: 'approvals', label: 'Approvals', icon: 'check-square', roles: ['super_admin', 'admin', 'reviewer'] },
      { key: 'users', label: 'Users', icon: 'users', roles: ['super_admin'] },
      { key: 'activity', label: 'Activity', icon: 'activity', roles: ['super_admin'] },
      { key: 'revenue', label: 'Revenue', icon: 'dollar-sign', roles: ['super_admin'] },
      { key: 'system', label: 'System', icon: 'server', roles: ['super_admin'] }
    ]

    const availableTabs = computed(() => {
      return allTabs.filter(tab => tab.roles.includes(userRole.value))
    })

    // ============ REPORT DATA REFS ============

    // Content Report
    const contentReport = ref({
      totalExams: 0, totalQuestions: 0, totalNotes: 0, totalFlashcards: 0,
      chartData: [], statusBreakdown: [], examBreakdown: []
    })

    // User Report (Super Admin)
    const userReport = ref({
      totalUsers: 0, activeUsers: 0, newUsers: 0, premiumUsers: 0,
      churnedUsers: 0, retentionRate: 0, usersTrend: 0,
      registrationChart: [], segments: [], topUsers: []
    })

    // Performance Report
    const perfReport = ref({
      totalTestsTaken: 0, avgScore: 0, passRate: 0, avgTimeMins: 0,
      scoreDistribution: [], difficultyBreakdown: [], testPerformance: []
    })

    // Question Report
    const questionReport = ref({
      totalQuestions: 0, avgCorrectRate: 0, hardestCount: 0, avgTimePerQ: 0,
      byDifficulty: [], byType: [], mostMissed: []
    })

    // Approval Report
    const approvalReport = ref({
      pending: 0, approved: 0, rejected: 0, avgReviewTime: '0h',
      chartData: [], byType: [], myReviews: [], reviewerPerformance: []
    })

    // Activity Report (Super Admin)
    const activityReport = ref({
      totalActions: 0, activeAdmins: 0, contentEdits: 0, totalLogins: 0,
      chartData: [], byAction: [], logs: [], adminList: []
    })

    // Revenue Report (Super Admin)
    const revenueReport = ref({
      totalRevenue: 0, totalSubscriptions: 0, renewalRate: 0, arpu: 0,
      revenueTrend: 0, chartData: [], planBreakdown: [], transactions: []
    })

    // System Report (Super Admin)
    const systemReport = ref({
      uptime: '0d', avgResponseTime: 0, errorCount: 0, storageUsed: '0 GB',
      apiChart: [], resources: [], errors: []
    })

    // ============ COMPUTED ============

    const hasData = computed(() => {
      switch (activeTab.value) {
        case 'content': return contentReport.value.examBreakdown.length > 0
        case 'users': return userReport.value.topUsers.length > 0
        case 'performance': return perfReport.value.testPerformance.length > 0
        case 'questions': return questionReport.value.mostMissed.length > 0
        case 'approvals': return approvalReport.value.chartData.length > 0
        case 'activity': return activityReport.value.logs.length > 0
        case 'revenue': return revenueReport.value.transactions.length > 0
        case 'system': return systemReport.value.apiChart.length > 0
        default: return false
      }
    })

    const filteredPerfData = computed(() => {
      if (!perfExamFilter.value) return perfReport.value.testPerformance
      return perfReport.value.testPerformance.filter(t => t.examId === perfExamFilter.value)
    })

    const filteredActivityLogs = computed(() => {
      if (!activityAdminFilter.value) return activityReport.value.logs
      return activityReport.value.logs.filter(l => l.adminId === activityAdminFilter.value)
    })

    // ============ HELPERS ============

    function normalizeChartData(data) {
      if (!data || !data.length) return []
      const max = Math.max(...data.map(d => d.value), 1)
      return data.map(d => ({ ...d, height: (d.value / max) * 100 }))
    }

    function normalizeStackedChart(data) {
      if (!data || !data.length) return []
      const max = Math.max(...data.map(d => (d.approved || 0) + (d.rejected || 0)), 1)
      return data.map(d => ({
        ...d,
        approvedHeight: ((d.approved || 0) / max) * 100,
        rejectedHeight: ((d.rejected || 0) / max) * 100
      }))
    }

    function formatRole(role) {
      if (!role) return ''
      return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    function formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    }

    function formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    }

    function timeAgo(date) {
      if (!date) return ''
      const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
      if (seconds < 60) return 'Just now'
      if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago'
      if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago'
      return Math.floor(seconds / 86400) + 'd ago'
    }

    // ============ API CALLS ============

    async function fetchContentReport() {
      try {
        const res = await api.get('/v1/admin/reports/content', { params: { days: globalDateRange.value } })
        const d = res.data
        contentReport.value = {
          totalExams: d.totalExams || 0,
          totalQuestions: d.totalQuestions || 0,
          totalNotes: d.totalNotes || 0,
          totalFlashcards: d.totalFlashcards || 0,
          chartData: normalizeChartData(d.chartData || []),
          statusBreakdown: d.statusBreakdown || [],
          examBreakdown: d.examBreakdown || []
        }
      } catch (e) { console.error('Failed to fetch content report:', e) }
    }

    async function fetchUserReport() {
      if (!isSuperAdmin.value) return
      try {
        const res = await api.get('/v1/admin/reports/users', { params: { days: globalDateRange.value } })
        const d = res.data
        userReport.value = {
          totalUsers: d.totalUsers || 0,
          activeUsers: d.activeUsers || 0,
          newUsers: d.newUsers || 0,
          premiumUsers: d.premiumUsers || 0,
          churnedUsers: d.churnedUsers || 0,
          retentionRate: d.retentionRate || 0,
          usersTrend: d.usersTrend || 0,
          registrationChart: normalizeChartData(d.registrationChart || []),
          segments: d.segments || [],
          topUsers: d.topUsers || []
        }
      } catch (e) { console.error('Failed to fetch user report:', e) }
    }

    async function fetchPerfReport() {
      try {
        const res = await api.get('/v1/admin/reports/performance', { params: { days: globalDateRange.value } })
        const d = res.data
        perfReport.value = {
          totalTestsTaken: d.totalTestsTaken || 0,
          avgScore: d.avgScore || 0,
          passRate: d.passRate || 0,
          avgTimeMins: d.avgTimeMins || 0,
          scoreDistribution: normalizeChartData(d.scoreDistribution || []),
          difficultyBreakdown: d.difficultyBreakdown || [],
          testPerformance: d.testPerformance || []
        }
        examOptions.value = d.examOptions || []
      } catch (e) { console.error('Failed to fetch performance report:', e) }
    }

    async function fetchQuestionReport() {
      try {
        const res = await api.get('/v1/admin/reports/questions', { params: { days: globalDateRange.value } })
        const d = res.data
        questionReport.value = {
          totalQuestions: d.totalQuestions || 0,
          avgCorrectRate: d.avgCorrectRate || 0,
          hardestCount: d.hardestCount || 0,
          avgTimePerQ: d.avgTimePerQ || 0,
          byDifficulty: d.byDifficulty || [],
          byType: d.byType || [],
          mostMissed: d.mostMissed || []
        }
      } catch (e) { console.error('Failed to fetch question report:', e) }
    }

    async function fetchApprovalReport() {
      if (!isAdmin.value && !isReviewer.value) return
      try {
        const res = await api.get('/v1/admin/reports/approvals', { params: { days: globalDateRange.value } })
        const d = res.data
        approvalReport.value = {
          pending: d.pending || 0,
          approved: d.approved || 0,
          rejected: d.rejected || 0,
          avgReviewTime: d.avgReviewTime || '0h',
          chartData: normalizeStackedChart(d.chartData || []),
          byType: d.byType || [],
          myReviews: d.myReviews || [],
          reviewerPerformance: d.reviewerPerformance || []
        }
      } catch (e) { console.error('Failed to fetch approval report:', e) }
    }

    async function fetchActivityReport() {
      if (!isSuperAdmin.value) return
      try {
        const res = await api.get('/v1/admin/reports/activity', { params: { days: globalDateRange.value } })
        const d = res.data
        activityReport.value = {
          totalActions: d.totalActions || 0,
          activeAdmins: d.activeAdmins || 0,
          contentEdits: d.contentEdits || 0,
          totalLogins: d.totalLogins || 0,
          chartData: normalizeChartData(d.chartData || []),
          byAction: d.byAction || [],
          logs: d.logs || [],
          adminList: d.adminList || []
        }
      } catch (e) { console.error('Failed to fetch activity report:', e) }
    }

    async function fetchRevenueReport() {
      if (!isSuperAdmin.value) return
      try {
        const res = await api.get('/v1/admin/reports/revenue', { params: { days: globalDateRange.value } })
        const d = res.data
        revenueReport.value = {
          totalRevenue: d.totalRevenue || 0,
          totalSubscriptions: d.totalSubscriptions || 0,
          renewalRate: d.renewalRate || 0,
          arpu: d.arpu || 0,
          revenueTrend: d.revenueTrend || 0,
          chartData: normalizeChartData(d.chartData || []),
          planBreakdown: d.planBreakdown || [],
          transactions: d.transactions || []
        }
      } catch (e) { console.error('Failed to fetch revenue report:', e) }
    }

    async function fetchSystemReport() {
      if (!isSuperAdmin.value) return
      try {
        const res = await api.get('/v1/admin/reports/system', { params: { days: globalDateRange.value } })
        const d = res.data
        systemReport.value = {
          uptime: d.uptime || '0d',
          avgResponseTime: d.avgResponseTime || 0,
          errorCount: d.errorCount || 0,
          storageUsed: d.storageUsed || '0 GB',
          apiChart: normalizeChartData(d.apiChart || []),
          resources: d.resources || [],
          errors: d.errors || []
        }
      } catch (e) { console.error('Failed to fetch system report:', e) }
    }

    // ============ FETCH BY TAB ============

    async function fetchTabData(tab) {
      loading.value = true
      try {
        switch (tab) {
          case 'content': await fetchContentReport(); break
          case 'users': await fetchUserReport(); break
          case 'performance': await fetchPerfReport(); break
          case 'questions': await fetchQuestionReport(); break
          case 'approvals': await fetchApprovalReport(); break
          case 'activity': await fetchActivityReport(); break
          case 'revenue': await fetchRevenueReport(); break
          case 'system': await fetchSystemReport(); break
        }
      } finally {
        loading.value = false
      }
    }

    async function refreshAll() {
      await fetchTabData(activeTab.value)
    }

    function onDateRangeChange() {
      fetchTabData(activeTab.value)
    }

    // Chart period changes
    async function changeContentChartPeriod(period) {
      contentChartPeriod.value = period
      try {
        const res = await api.get('/v1/admin/reports/content/chart', { params: { period } })
        contentReport.value.chartData = normalizeChartData(res.data.chartData || [])
      } catch (e) { console.error('Failed to fetch content chart:', e) }
    }

    async function changeUserChartPeriod(period) {
      userChartPeriod.value = period
      try {
        const res = await api.get('/v1/admin/reports/users/chart', { params: { period } })
        userReport.value.registrationChart = normalizeChartData(res.data.chartData || [])
      } catch (e) { console.error('Failed to fetch user chart:', e) }
    }

    async function changeRevenueChartPeriod(period) {
      revenueChartPeriod.value = period
      try {
        const res = await api.get('/v1/admin/reports/revenue/chart', { params: { period } })
        revenueReport.value.chartData = normalizeChartData(res.data.chartData || [])
      } catch (e) { console.error('Failed to fetch revenue chart:', e) }
    }

    // Watch tab changes
    watch(activeTab, (newTab) => { fetchTabData(newTab) })

    // ============ CSV EXPORT ============

    function downloadCSV(filename, headers, rows) {
      const csv = [headers, ...rows].map(row => row.map(c => `"${c}"`).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${filename}-${Date.now()}.csv`
      a.click()
      URL.revokeObjectURL(url)
    }

    function exportCurrentReport() {
      switch (activeTab.value) {
        case 'content': exportContentReport(); break
        case 'users': exportUserReport(); break
        case 'performance': exportPerfReport(); break
        case 'questions': exportQuestionReport(); break
        case 'approvals': exportApprovalReport(); break
        case 'activity': exportActivityReport(); break
        case 'revenue': exportRevenueReport(); break
        case 'system': exportSystemReport(); break
      }
    }

    function exportContentReport() {
      downloadCSV('content-report',
        ['Exam', 'Subjects', 'Topics', 'Lessons', 'Questions', 'Notes', 'Status'],
        contentReport.value.examBreakdown.map(i => [i.name, i.subjects, i.topics, i.lessons, i.questions, i.notes, i.status])
      )
    }

    function exportUserReport() {
      downloadCSV('user-report',
        ['Name', 'Email', 'Tests Taken', 'Avg Score', 'Last Active', 'Plan'],
        userReport.value.topUsers.map(u => [u.name, u.email, u.testsTaken, u.avgScore, u.lastActive, u.plan])
      )
    }

    function exportPerfReport() {
      downloadCSV('performance-report',
        ['Test', 'Exam', 'Attempts', 'Avg Score', 'Pass Rate', 'Avg Time', 'Completion'],
        filteredPerfData.value.map(t => [t.name, t.examName, t.attempts, t.avgScore, t.passRate, t.avgTimeMins, t.completionRate])
      )
    }

    function exportQuestionReport() {
      downloadCSV('question-report',
        ['Question', 'Subject', 'Difficulty', 'Attempts', 'Correct %', 'Avg Time'],
        questionReport.value.mostMissed.map(q => [q.text, q.subject, q.difficulty, q.attempts, q.correctRate, q.avgTime])
      )
    }

    function exportApprovalReport() {
      downloadCSV('approval-report',
        ['Reviewer', 'Reviewed', 'Approved', 'Rejected', 'Avg Time', 'Accuracy'],
        approvalReport.value.reviewerPerformance.map(r => [r.name, r.totalReviewed, r.approved, r.rejected, r.avgTime, r.accuracy])
      )
    }

    function exportActivityReport() {
      downloadCSV('activity-report',
        ['Admin', 'Action', 'Resource', 'Details', 'IP', 'Timestamp'],
        filteredActivityLogs.value.map(l => [l.adminName, l.action, l.resource, l.details, l.ipAddress, l.timestamp])
      )
    }

    function exportRevenueReport() {
      downloadCSV('revenue-report',
        ['User', 'Plan', 'Amount', 'Status', 'Payment Method', 'Date'],
        revenueReport.value.transactions.map(t => [t.userName, t.plan, t.amount, t.status, t.paymentMethod, t.date])
      )
    }

    function exportSystemReport() {
      downloadCSV('system-report',
        ['Error', 'Endpoint', 'Status Code', 'Count', 'Last Occurred'],
        systemReport.value.errors.map(e => [e.message, e.endpoint, e.statusCode, e.count, e.lastOccurred])
      )
    }

    // ============ LIFECYCLE ============

    onMounted(() => { fetchTabData(activeTab.value) })

    return {
      // Auth
      user, userRole, isSuperAdmin, isAdmin, isReviewer,
      breadcrumbs, loading, currentDate,
      // Tabs
      activeTab, availableTabs, globalDateRange,
      // Chart periods
      contentChartPeriod, userChartPeriod, revenueChartPeriod,
      // Filters
      perfExamFilter, activityAdminFilter, examOptions,
      // Data
      contentReport, userReport, perfReport, questionReport,
      approvalReport, activityReport, revenueReport, systemReport,
      // Computed
      hasData, filteredPerfData, filteredActivityLogs,
      // Methods
      refreshAll, onDateRangeChange,
      changeContentChartPeriod, changeUserChartPeriod, changeRevenueChartPeriod,
      exportCurrentReport, exportContentReport, exportUserReport,
      exportPerfReport, exportQuestionReport, exportApprovalReport,
      exportActivityReport, exportRevenueReport, exportSystemReport,
      // Helpers
      formatRole, formatDate, formatDateTime, timeAgo
    }
  }
}
</script>

<style scoped>
/* Report Header */
.report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #003049 0%, #00506e 100%);
  border-radius: 12px;
  color: white;
}
.report-header-left { display: flex; align-items: center; gap: 12px; }
.report-date { font-size: 13px; opacity: 0.8; }
.report-header-right select {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
}
.report-header-right select option { color: #111; }

/* Role Badge */
.role-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.role-super_admin { background: rgba(168, 85, 247, 0.2); color: #a855f7; }
.role-admin { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.role-reviewer { background: rgba(34, 197, 94, 0.2); color: #22c55e; }
.role-content_manager { background: rgba(245, 158, 11, 0.2); color: #f59e0b; }

/* Report Tabs */
.report-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 12px;
  overflow-x: auto;
}
.report-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}
.report-tab:hover { color: #111827; background: rgba(255,255,255,0.6); }
.report-tab.active {
  background: white;
  color: #003049;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  font-weight: 600;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  gap: 12px;
  margin-bottom: 4px;
}
.stats-4 { grid-template-columns: repeat(4, 1fr); }
.stats-6 { grid-template-columns: repeat(6, 1fr); }

.stat-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  position: relative;
  cursor: default;
}
.stat-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.stat-text { min-width: 0; }
.stat-value { display: block; font-size: 20px; font-weight: 700; color: #111827; line-height: 1.2; }
.stat-label { display: block; font-size: 11px; color: #6b7280; }

.stat-primary .stat-icon { background: rgba(59,130,246,0.1); color: #3b82f6; }
.stat-success .stat-icon { background: rgba(34,197,94,0.1); color: #22c55e; }
.stat-warning .stat-icon { background: rgba(245,158,11,0.1); color: #f59e0b; }
.stat-info .stat-icon { background: rgba(6,182,212,0.1); color: #06b6d4; }
.stat-purple .stat-icon { background: rgba(168,85,247,0.1); color: #a855f7; }
.stat-rose .stat-icon { background: rgba(244,63,94,0.1); color: #f43f5e; }
.stat-danger .stat-icon { background: rgba(239,68,68,0.1); color: #ef4444; }
.stat-cyan .stat-icon { background: rgba(6,182,212,0.1); color: #06b6d4; }

.stat-trend {
  position: absolute;
  top: 10px; right: 12px;
  font-size: 11px; font-weight: 600;
  display: flex; align-items: center; gap: 2px;
}
.trend-up { color: #22c55e; }
.trend-down { color: #ef4444; }

/* Cards */
.card {
  background: white;
  border: none;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.card-header {
  padding: 14px 16px;
  background: transparent;
  border-bottom: 1px solid #f3f4f6;
}
.card-title { font-size: 14px; font-weight: 600; color: #111827; }
.card-body { padding: 16px; }

/* Chart */
.chart-container { padding: 8px 0; }
.mini-chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 160px;
  padding: 0 4px;
}
.chart-bar-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
}
.chart-bar {
  width: 100%;
  min-height: 4px;
  background: linear-gradient(180deg, #003049, #00506e);
  border-radius: 4px 4px 0 0;
  position: relative;
  transition: height 0.5s ease;
  cursor: pointer;
}
.chart-bar:hover { opacity: 0.85; }
.chart-bar-blue { background: linear-gradient(180deg, #3b82f6, #60a5fa); }
.chart-bar-green { background: linear-gradient(180deg, #22c55e, #4ade80); }
.chart-bar-red { background: linear-gradient(180deg, #ef4444, #f87171); }
.chart-bar-yellow { background: linear-gradient(180deg, #f59e0b, #fbbf24); }
.chart-bar-purple { background: linear-gradient(180deg, #a855f7, #c084fc); }
.bar-tooltip {
  display: none;
  position: absolute;
  top: -24px; left: 50%;
  transform: translateX(-50%);
  background: #111827;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
}
.chart-bar:hover .bar-tooltip { display: block; }
.bar-label { font-size: 10px; color: #9ca3af; margin-top: 4px; }

/* Stacked chart */
.chart-bar-stack {
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  height: 100%;
  justify-content: flex-start;
}
.chart-bar-stack .chart-bar { border-radius: 0; }
.chart-bar-stack .chart-bar:last-child { border-radius: 4px 4px 0 0; }

/* Chart period buttons */
.chart-period-btns { display: flex; gap: 4px; }
.period-btn {
  padding: 3px 10px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 6px;
  font-size: 11px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}
.period-btn:hover { border-color: #003049; color: #003049; }
.period-btn.active { background: #003049; color: white; border-color: #003049; }

/* Breakdown list */
.breakdown-list { display: flex; flex-direction: column; gap: 12px; }
.breakdown-item { display: grid; grid-template-columns: 1fr auto; gap: 4px 8px; align-items: center; }
.breakdown-label { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151; }
.breakdown-value { font-size: 13px; font-weight: 600; color: #111827; text-align: right; }
.breakdown-bar { grid-column: 1 / -1; height: 6px; background: #f3f4f6; border-radius: 3px; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 3px; transition: width 0.5s ease; }
.breakdown-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

.dot-green, .bar-green { background: #22c55e; }
.dot-yellow, .bar-yellow { background: #f59e0b; }
.dot-blue, .bar-blue { background: #3b82f6; }
.dot-gray, .bar-gray { background: #9ca3af; }
.dot-red, .bar-red { background: #ef4444; }
.dot-purple, .bar-purple { background: #a855f7; }
.dot-success, .bar-success { background: #22c55e; }
.dot-warning, .bar-warning { background: #f59e0b; }
.dot-info, .bar-info { background: #06b6d4; }
.dot-danger, .bar-danger { background: #ef4444; }
.dot-primary, .bar-primary { background: #3b82f6; }

/* Tables */
.table { font-size: 13px; }
.table th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-bottom: 2px solid #f3f4f6;
  padding: 10px 14px;
}
.table td { padding: 10px 14px; vertical-align: middle; color: #374151; }
.table-hover tbody tr:hover { background: #f9fafb; }

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}
.status-active { background: rgba(34,197,94,0.1); color: #22c55e; }
.status-draft { background: rgba(245,158,11,0.1); color: #f59e0b; }
.status-archived { background: rgba(239,68,68,0.1); color: #ef4444; }

/* Difficulty badges */
.difficulty-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}
.diff-easy { background: rgba(34,197,94,0.1); color: #22c55e; }
.diff-medium { background: rgba(245,158,11,0.1); color: #f59e0b; }
.diff-hard { background: rgba(239,68,68,0.1); color: #ef4444; }

/* Action badges */
.action-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}
.action-create { background: rgba(34,197,94,0.1); color: #22c55e; }
.action-update { background: rgba(59,130,246,0.1); color: #3b82f6; }
.action-delete { background: rgba(239,68,68,0.1); color: #ef4444; }
.action-login { background: rgba(168,85,247,0.1); color: #a855f7; }
.action-approve { background: rgba(6,182,212,0.1); color: #06b6d4; }

/* Score bar inline */
.score-bar-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}
.score-bar-inline .score-fill {
  height: 6px;
  border-radius: 3px;
  flex: 1;
  background: #e5e7eb;
  position: relative;
}
.score-bar-inline .score-fill::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  border-radius: 3px;
}
.fill-green { background: #22c55e; }
.fill-yellow { background: #f59e0b; }
.fill-red { background: #ef4444; }
.fill-primary { background: #3b82f6; }
.fill-danger { background: #ef4444; }

/* Resource bars */
.resource-bar {
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
}
.resource-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}
.resource-fill.fill-primary { background: #3b82f6; }
.resource-fill.fill-danger { background: #ef4444; }
.resource-fill.fill-blue { background: #06b6d4; }

/* Filter */
.filter-select-modern {
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  background: white;
  cursor: pointer;
}
.filter-sm { padding: 4px 10px; font-size: 12px; }

/* Loading */
.report-loading {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 100;
  font-size: 14px;
  color: #6b7280;
}
.spinner {
  width: 32px; height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #003049;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.spin { animation: spin 1s linear infinite; }

/* Responsive */
@media (max-width: 1200px) {
  .stats-6 { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 768px) {
  .stats-4 { grid-template-columns: repeat(2, 1fr); }
  .stats-6 { grid-template-columns: repeat(2, 1fr); }
  .report-header { flex-direction: column; gap: 8px; align-items: flex-start; }
  .report-tabs { gap: 2px; }
  .report-tab { padding: 6px 10px; font-size: 12px; }
  .stat-value { font-size: 16px; }
}
@media (max-width: 480px) {
  .stats-4, .stats-6 { grid-template-columns: 1fr 1fr; }
}

/* Dark mode */
:global(body.dark-mode) .report-tabs { background: #1f2937; }
:global(body.dark-mode) .report-tab { color: #9ca3af; }
:global(body.dark-mode) .report-tab.active { background: #374151; color: #f3f4f6; }
:global(body.dark-mode) .stat-card { background: #1f2937; }
:global(body.dark-mode) .stat-value { color: #f3f4f6; }
:global(body.dark-mode) .card { background: #1f2937; }
:global(body.dark-mode) .card-header { border-bottom-color: #374151; }
:global(body.dark-mode) .card-title { color: #f3f4f6; }
:global(body.dark-mode) .table th { color: #9ca3af; border-bottom-color: #374151; }
:global(body.dark-mode) .table td { color: #d1d5db; }
:global(body.dark-mode) .table-hover tbody tr:hover { background: #374151; }
:global(body.dark-mode) .breakdown-label { color: #d1d5db; }
:global(body.dark-mode) .breakdown-value { color: #f3f4f6; }
:global(body.dark-mode) .breakdown-bar { background: #374151; }
:global(body.dark-mode) .filter-select-modern { background: #374151; border-color: #4b5563; color: #d1d5db; }
:global(body.dark-mode) .report-loading { background: rgba(17,24,39,0.8); }
:global(body.dark-mode) .period-btn { background: #374151; border-color: #4b5563; color: #9ca3af; }
:global(body.dark-mode) .resource-bar { background: #374151; }
</style>